import { ref, computed, watch } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import { PROFILE_HIERARCHY } from '../profileHierarchy'

const t = key => i18n.t(key)

const buildSearchTerms = (...parts) => parts.join(' ').toLowerCase()

const getCurrentUserRole = () => {
  if (typeof localStorage === 'undefined' || !localStorage.userData) return null
  try {
    return JSON.parse(localStorage.userData).role || null
  } catch {
    return null
  }
}

const getAllowedProfileIds = userRole => {
  const index = PROFILE_HIERARCHY.indexOf(userRole)

  // SYSADMIN e perfis fora da hierarquia visualizam todos
  if (index === -1) return new Set(PROFILE_HIERARCHY)

  return new Set(PROFILE_HIERARCHY.slice(index))
}

const PROFILE_BADGE = {
  badgeBg: '#F1EFE8',
  badgeColor: '#444441',
  badgeBorder: '#B4B2A9',
}

export default function useUserProfileGuide() {
  const isMainOpen = ref(false)
  const searchQuery = ref('')
  const expandedProfile = ref(null)
  const currentUserRole = getCurrentUserRole()

  const profiles = computed(() => {
    // Garante reatividade ao trocar o idioma
    // eslint-disable-next-line no-unused-expressions
    i18n.locale

    return [
    {
      id: 'TRCKOWN',
      label: t('Tracking Owner'),
      desc: t('Broad access'),
      searchTerms: buildSearchTerms(t('Tracking Owner'), t('Broad access')),
      ...PROFILE_BADGE,
      sections: [
        {
          title: t('Visible menu'),
          tags: [
            { label: t('Assets'), type: 'on' }, { label: t('Map'), type: 'on' }, { label: t('Users'), type: 'on' },
            { label: t('Customers'), type: 'on' }, { label: t('Dealers'), type: 'on' }, { label: t('Devices'), type: 'on' },
            { label: t('Drivers'), type: 'on' }, { label: t('Reports'), type: 'on' }, { label: t('Management'), type: 'on' },
            { label: t('Maintenance'), type: 'on' },
          ],
        },
        {
          title: t('Can create / edit / delete'),
          tags: [
            { label: t('Users'), type: 'on' }, { label: t('Customers'), type: 'on' }, { label: t('Dealers'), type: 'on' },
            { label: t('Devices'), type: 'on' }, { label: t('Assets'), type: 'on' }, { label: t('Group'), type: 'on' },
          ],
        },
      ],
    },
    {
      id: 'TRCKOWNVI',
      label: t('Tracking Owner Viewer'),
      desc: t('View only'),
      searchTerms: buildSearchTerms(t('Tracking Owner Viewer'), t('View only')),
      ...PROFILE_BADGE,
      sections: [
        {
          title: t('Visible menu'),
          tags: [{ label: t('Same as Tracking Owner'), type: 'on' }],
        },
        {
          title: t('Restrictions'),
          tags: [
            { label: t('Save button hidden in all forms'), type: 'vi' },
            { label: t('Create button hidden in lists'), type: 'vi' },
          ],
        },
      ],
      note: t('Viewer profiles read only note'),
    },
    {
      id: 'SERVSHAR',
      label: t('Service Provider'),
      desc: t('No devices access'),
      searchTerms: buildSearchTerms(t('Service Provider'), t('No devices access')),
      ...PROFILE_BADGE,
      sections: [
        {
          title: t('Visible menu'),
          tags: [
            { label: t('Assets'), type: 'on' }, { label: t('Map'), type: 'on' }, { label: t('Users'), type: 'on' },
            { label: t('Customers'), type: 'on' }, { label: t('Dealers'), type: 'on' }, { label: t('Drivers'), type: 'on' },
            { label: t('Reports'), type: 'on' }, { label: t('Management'), type: 'on' }, { label: t('Maintenance'), type: 'on' },
            { label: t('Devices'), type: 'off' }
          ],
        },
        {
          title: t('Can create / edit / delete'),
          tags: [
            { label: t('Users'), type: 'on' }, { label: t('Customers'), type: 'on' },
            { label: t('Dealers'), type: 'on' }, { label: t('Assets'), type: 'on' },
          ],
        },
      ],
    },
    {
      id: 'SERVSHARVI',
      label: t('Service Provider Viewer'),
      desc: t('View only'),
      searchTerms: buildSearchTerms(t('Service Provider Viewer'), t('View only')),
      ...PROFILE_BADGE,
      sections: [
        {
          title: t('Visible menu'),
          tags: [{ label: t('Same as Service Provider'), type: 'on' }],
        },
        {
          title: t('Restrictions'),
          tags: [
            { label: t('Save button hidden in all forms'), type: 'vi' },
            { label: t('Create button hidden in lists'), type: 'vi' },
          ],
        },
      ],
    },
    {
      id: 'DEALER',
      label: t('Dealer role'),
      desc: t('No dealers or devices'),
      searchTerms: buildSearchTerms(t('Dealer role'), t('No dealers or devices')),
      ...PROFILE_BADGE,
      sections: [
        {
          title: t('Visible menu'),
          tags: [
            { label: t('Assets'), type: 'on' }, { label: t('Map'), type: 'on' }, { label: t('Users'), type: 'on' },
            { label: t('Customers'), type: 'on' }, { label: t('Drivers'), type: 'on' }, { label: t('Reports'), type: 'on' },
            { label: t('Management'), type: 'on' }, { label: t('Maintenance'), type: 'on' }, { label: t('Dealers'), type: 'off' },
            { label: t('Devices'), type: 'off' }
          ],
        },
        {
          title: t('Can create / edit / delete'),
          tags: [
            { label: t('Users'), type: 'on' }, { label: t('Customers'), type: 'on' }, { label: t('Assets'), type: 'on' },
          ],
        },
      ],
    },
    {
      id: 'FINACON',
      label: t('Final Consumer'),
      desc: t('Cannot register assets'),
      searchTerms: buildSearchTerms(t('Final Consumer'), t('Cannot register assets')),
      ...PROFILE_BADGE,
      sections: [
        {
          title: t('Visible menu'),
          tags: [
            { label: t('Assets'), type: 'on' }, { label: t('Map'), type: 'on' }, { label: t('Users'), type: 'on' },
            { label: t('Drivers'), type: 'on' }, { label: t('Reports'), type: 'on' }, { label: t('Management'), type: 'on' },
            { label: t('Maintenance'), type: 'on' }, { label: t('Customers'), type: 'off' }, { label: t('Dealers'), type: 'off' },
            { label: t('Devices'), type: 'off' },
          ],
        },
        {
          title: t('Asset restrictions'),
          tags: [
            { label: t('Cannot create or edit assets'), type: 'off' },
            { label: t('Asset fields disabled'), type: 'off' },
          ],
        },
      ],
    },
    {
      id: 'FINACONVI',
      label: t('Final Consumer Viewer'),
      desc: t('View only'),
      searchTerms: buildSearchTerms(t('Final Consumer Viewer'), t('View only')),
      ...PROFILE_BADGE,
      sections: [
        {
          title: t('Restrictions'),
          tags: [
            { label: t('Same as Final Consumer'), type: 'vi' },
            { label: t('Save button hidden in all forms'), type: 'vi' },
            { label: t('Cannot create assets'), type: 'off' },
          ],
        },
      ],
    },
    {
      id: 'SIMPlVI',
      label: t('Simple Viewer'),
      desc: t('Assets and map only'),
      searchTerms: buildSearchTerms(t('Simple Viewer'), t('Assets and map only')),
      ...PROFILE_BADGE,
      sections: [
        {
          title: t('Visible menu'),
          tags: [
            { label: t('Assets'), type: 'on' }, { label: t('Map'), type: 'on' }, { label: t('All other menu items'), type: 'off' },
          ],
        },
        {
          title: t('Restrictions'),
          tags: [
            { label: t('No access to users, customers, reports, management, maintenance'), type: 'off' },
            { label: t('Alarms menu hidden'), type: 'off' },
          ],
        },
      ],
      note: t('Most restricted profile note'),
    },
  ]
  })

  const visibleProfiles = computed(() => {
    const allowedIds = getAllowedProfileIds(currentUserRole)
    return profiles.value.filter(profile => allowedIds.has(profile.id))
  })

  const filteredProfiles = computed(() => {
    let list = visibleProfiles.value

    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase().trim()
      list = list.filter(
          p => p.label.toLowerCase().includes(q) || p.searchTerms.includes(q),
      )
    }

    return list
  })

  watch(visibleProfiles, list => {
    if (expandedProfile.value && !list.some(p => p.id === expandedProfile.value)) {
      expandedProfile.value = null
    }
  })

  const toggleMain = () => {
    isMainOpen.value = !isMainOpen.value
  }

  const toggleProfile = id => {
    expandedProfile.value = expandedProfile.value === id ? null : id
  }

  return {
    isMainOpen,
    searchQuery,
    expandedProfile,
    profiles,
    visibleProfiles,
    filteredProfiles,
    toggleMain,
    toggleProfile,
  }
}
